// Mobile (<900px) card for `/russia-rates`. Mirrors the China
// `RequestMobileCard` interaction model: an interactive `<article>`
// with `onClick` + Enter/Space keyboard activation (not a `<Link>`).
// Vertical stack uses china-style rhythm with russia-specific fields:
//   row 1 — ID + RequestStatusPill + RussiaRequestTypeChip
//   row 2 — name (16px, bold) with trailing manager (12px muted)
//   row 3 — start-city chips · → endAddress (truncated)
//   row 4 — primary-bid summary block
//
// URGENT-typed cards carry the same `--status-error-bg` tint as the
// desktop row.

import { useTranslation } from 'react-i18next';
import { RussiaRequestType, type RussiaRequest } from '@magimp/shared';

import { Icon } from '../../../ui/index.js';
import { NotificationDot } from '../../notifications/index.js';
import { RequestStatusPill } from '../../requests/display/index.js';
import { RussiaRequestPrimaryBidCell } from './RussiaRequestPrimaryBidCell.js';
import { RussiaRequestTypeChip } from './RussiaRequestTypeChip.js';
import { StartCityChips } from './StartCityChips.js';

interface RussiaRequestMobileCardProps {
  row: RussiaRequest;
  language: string;
  onOpen: () => void;
  notificationCount?: number;
}

export function RussiaRequestMobileCard({
  row,
  language,
  onOpen,
  notificationCount,
}: RussiaRequestMobileCardProps) {
  const { t } = useTranslation('russiaRequests');
  const isUrgent = row.type === RussiaRequestType.URGENT;
  return (
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <article
      aria-label={row.name}
      data-testid={`russia-requests-card-${row.id}`}
      data-urgent={isUrgent ? 'true' : undefined}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: isUrgent ? 'var(--status-error-bg)' : 'var(--bg-1)',
        color: 'var(--fg-1)',
        cursor: 'pointer',
        outline: 'none',
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>{row.id}</span>
        <span style={{ flex: 1 }} />
        <RequestStatusPill status={row.status} size="sm" />
        <RussiaRequestTypeChip type={row.type} />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--fg-1)',
        }}
      >
        <span
          style={{
            flex: '1 1 0',
            minWidth: 0,
            wordBreak: 'break-word',
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span>{row.name}</span>
          {notificationCount !== undefined && notificationCount > 0 ? (
            <NotificationDot count={notificationCount} />
          ) : null}
        </span>
        <span
          data-testid={`russia-requests-card-manager-${row.id}`}
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            whiteSpace: 'nowrap',
          }}
        >
          {row.manager}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--fg-2)',
          flexWrap: 'wrap',
        }}
      >
        <StartCityChips
          startCities={row.startCities}
          testIdPrefix={`russia-requests-card-start-cities-${row.id}`}
        />
        <Icon name="arrow-right" size={11} />
        <span
          style={{
            flex: '1 1 0',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.endAddress}
        >
          {row.endAddress}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          paddingTop: 4,
          borderTop: '1px solid var(--border-2)',
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: 'var(--fg-3)',
            textTransform: 'uppercase',
          }}
        >
          {t('columns.primaryBidSummary')}
        </span>
        <RussiaRequestPrimaryBidCell
          summary={row.primaryBidSummary}
          rowId={row.id}
          i18nLanguage={language}
        />
      </div>
    </article>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
}
